// src/components/Dashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { dashboardService } from './api';
import './dashboard.css';

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [topAccounts, setTopAccounts] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(6);
  const toast = useRef(null);

  // Chart data states
  const [lineChartData, setLineChartData] = useState({});
  const [pieChartData, setPieChartData] = useState({});
  const [barChartData, setBarChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  useEffect(() => {
    if (monthlyData.length > 0 && topAccounts.length > 0) {
      setupCharts();
    }
  }, [monthlyData, topAccounts]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [summaryRes, monthlyRes, topAccountsRes, recentRes] = await Promise.all([
        dashboardService.getSummary(),
        dashboardService.getMonthlySummary(selectedPeriod),
        dashboardService.getTopAccounts(5),
        dashboardService.getRecentTransactions(10),
      ]);

      setSummary(summaryRes.data);
      setMonthlyData(monthlyRes.data);
      setTopAccounts(topAccountsRes.data);
      
      // Process transactions to ensure dates are properly formatted
      const processedTransactions = recentRes.data.map(transaction => ({
        ...transaction,
        // Handle different date formats from API
        transactionDate: formatDateFromAPI(transaction.transactionDate)
      }));
      setRecentTransactions(processedTransactions);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch dashboard data',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format dates from API
  const formatDateFromAPI = (dateValue) => {
    if (!dateValue) return new Date();
    
    // If it's already a Date object
    if (dateValue instanceof Date) return dateValue;
    
    // Handle string dates
    if (typeof dateValue === 'string') {
      // Try to parse ISO format (2024-01-15T00:00:00)
      let parsedDate = new Date(dateValue);
      if (!isNaN(parsedDate.getTime())) return parsedDate;
      
      // Try to parse DD/MM/YYYY format
      const parts = dateValue.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        parsedDate = new Date(year, month, day);
        if (!isNaN(parsedDate.getTime())) return parsedDate;
      }
      
      // Try to parse YYYY-MM-DD format
      const dashParts = dateValue.split('-');
      if (dashParts.length === 3) {
        const year = parseInt(dashParts[0], 10);
        const month = parseInt(dashParts[1], 10) - 1;
        const day = parseInt(dashParts[2], 10);
        parsedDate = new Date(year, month, day);
        if (!isNaN(parsedDate.getTime())) return parsedDate;
      }
    }
    
    // Handle numeric timestamps
    if (typeof dateValue === 'number') {
      return new Date(dateValue);
    }
    
    // Return current date as fallback
    return new Date();
  };

  // Format date for display
  const formatDisplayDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    return dateObj.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format time for display
  const formatDisplayTime = (date) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    
    return dateObj.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
    toast.current.show({
      severity: 'success',
      summary: 'Success',
      detail: 'Dashboard data refreshed',
      life: 2000
    });
  };

  const setupCharts = () => {
    // Line Chart Configuration
    const lineData = {
      labels: monthlyData.map(item => item.month || item.Month),
      datasets: [
        {
          label: 'Debit',
          data: monthlyData.map(item => item.totalDebit || item.TotalDebit || 0),
          fill: true,
          borderColor: '#42A5F5',
          backgroundColor: 'rgba(66, 165, 245, 0.1)',
          tension: 0.4,
          pointBackgroundColor: '#42A5F5',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Credit',
          data: monthlyData.map(item => item.totalCredit || item.TotalCredit || 0),
          fill: true,
          borderColor: '#FFA726',
          backgroundColor: 'rgba(255, 167, 38, 0.1)',
          tension: 0.4,
          pointBackgroundColor: '#FFA726',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        }
      ]
    };

    // Pie Chart Configuration
    const pieData = {
      labels: topAccounts.map(account => account.ac_name || account.acName || account.AcName),
      datasets: [
        {
          data: topAccounts.map(account => Math.abs(account.balance || account.Balance || 0)),
          backgroundColor: [
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
            '#FF9F40', '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0'
          ],
          hoverBackgroundColor: [
            '#FF6384CC', '#36A2EBCC', '#FFCE56CC', '#4BC0C0CC', '#9966FFCC',
            '#FF9F40CC', '#FF6384CC', '#36A2EBCC', '#FFCE56CC', '#4BC0C0CC'
          ],
          borderWidth: 2,
          borderColor: '#fff'
        }
      ]
    };

    // Bar Chart for Account Balances
    const barData = {
      labels: topAccounts.slice(0, 5).map(account => account.ac_name || account.acName || account.AcName),
      datasets: [
        {
          label: 'Balance',
          data: topAccounts.slice(0, 5).map(account => Math.abs(account.balance || account.Balance || 0)),
          backgroundColor: 'rgba(76, 175, 80, 0.8)',
          borderRadius: 8,
          barPercentage: 0.7,
          categoryPercentage: 0.8,
        }
      ]
    };

    // Chart Options
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              family: 'Inter, sans-serif'
            }
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              label += new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(context.raw);
              return label;
            }
          },
          backgroundColor: 'rgba(0,0,0,0.8)',
          padding: 12,
          titleFont: { size: 14, weight: 'bold' },
          bodyFont: { size: 13 },
        }
      },
      scales: {
        y: {
          ticks: {
            callback: function(value) {
              return '₹' + value.toLocaleString('en-IN');
            },
            font: { size: 11 }
          },
          grid: {
            color: 'rgba(0,0,0,0.05)',
            drawBorder: true,
          },
          title: {
            display: true,
            text: 'Amount (₹)',
            font: { size: 12, weight: 'bold' }
          }
        },
        x: {
          ticks: {
            font: { size: 11 },
            rotation: 0,
          },
          grid: {
            display: false
          }
        }
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      elements: {
        line: {
          borderWidth: 2,
        },
        point: {
          hoverRadius: 8,
        }
      }
    };

    setLineChartData(lineData);
    setPieChartData(pieData);
    setBarChartData(barData);
    setChartOptions(options);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCompactCurrency = (value) => {
    if (!value && value !== 0) return '₹0';
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    }
    if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    }
    if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return formatCurrency(value);
  };

  const transactionDateTemplate = (rowData) => {
    const date = rowData.transactionDate;
    if (!date) return <span className="text-500">N/A</span>;
    
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return <span className="text-500">Invalid Date</span>;
    
    return (
      <div className="flex flex-column">
        <span className="font-bold">{formatDisplayDate(dateObj)}</span>
        <span className="text-xs text-500">{formatDisplayTime(dateObj)}</span>
      </div>
    );
  };

  const transactionAmountTemplate = (rowData) => {
    const amount = rowData.totalAmount || rowData.TotalAmount || 0;
    return (
      <span className={`font-bold ${amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
        {formatCurrency(amount)}
      </span>
    );
  };

  const statusTemplate = (rowData) => {
    const cancelled = rowData.cancelled || rowData.Cancelled;
    const posted = rowData.posted || rowData.Posted;
    
    if (cancelled === 1) {
      return <Tag severity="danger" value="Cancelled" icon="pi pi-times-circle" rounded />;
    }
    if (posted === 1) {
      return <Tag severity="success" value="Posted" icon="pi pi-check-circle" rounded />;
    }
    return <Tag severity="warning" value="Pending" icon="pi pi-clock" rounded />;
  };

  const voucherTemplate = (rowData) => {
    const voucherNo = rowData.voucherRefNo || rowData.VoucherRefNo || rowData.voucher_ref_no;
    return (
      <div className="flex align-items-center gap-2">
        <i className="pi pi-receipt text-primary"></i>
        <span className="font-mono">{voucherNo || 'N/A'}</span>
      </div>
    );
  };

  const StatCard = ({ title, value, subtitle, icon, color, trend, trendValue }) => (
    <Card className="stat-card" style={{ 
      background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
      color: 'white',
      cursor: 'pointer',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease'
    }}>
      <div className="flex justify-content-between align-items-start">
        <div className="flex-1">
          <div className="text-sm opacity-80 mb-2">{title}</div>
          <div className="text-2xl font-bold mb-1">{formatCompactCurrency(value)}</div>
          {subtitle && <div className="text-xs opacity-80">{subtitle}</div>}
          {trend && (
            <div className={`text-xs mt-2 ${trendValue >= 0 ? 'text-green-200' : 'text-red-200'}`}>
              <i className={`pi pi-arrow-${trendValue >= 0 ? 'up' : 'down'} mr-1`}></i>
              {Math.abs(trendValue)}% vs last month
            </div>
          )}
        </div>
        <div className="stat-icon">
          <i className={`${icon} text-4xl opacity-50`}></i>
        </div>
      </div>
    </Card>
  );

  const LoadingSkeleton = () => (
    <div className="grid">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="col-12 md:col-6 lg:col-4">
          <Card>
            <Skeleton width="60%" height="2rem" className="mb-2" />
            <Skeleton width="40%" height="3rem" />
          </Card>
        </div>
      ))}
    </div>
  );

  if (loading && !refreshing) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="dashboard-container">
      <Toast ref={toast} />
      
      {/* Header Section */}
      <div className="dashboard-header mb-4 flex justify-content-between align-items-center flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-primary m-0">Financial Dashboard</h1>
          <p className="text-500 mt-1">Welcome back! Here's your financial overview</p>
        </div>
        <div className="flex gap-2 mt-3 md:mt-0">
          <Button 
            label={`${selectedPeriod} Months`} 
            icon="pi pi-calendar" 
            className="p-button-outlined"
            onClick={() => {
              const periods = [3, 6, 12];
              const currentIndex = periods.indexOf(selectedPeriod);
              const nextPeriod = periods[(currentIndex + 1) % periods.length];
              setSelectedPeriod(nextPeriod);
            }}
          />
          <Button 
            icon="pi pi-refresh" 
            className="p-button-rounded p-button-text"
            onClick={refreshData}
            loading={refreshing}
            tooltip="Refresh Data"
          />
        </div>
      </div>

      {/* Stats Cards Grid */}
      <div className="grid">
        <div className="col-12 md:col-6 lg:col-4 xl:col-2">
          <StatCard 
            title="Total Debit" 
            value={summary?.totalDebit || summary?.TotalDebit || 0} 
            icon="pi pi-arrow-up" 
            color="#2196F3"
            trend={true}
            trendValue={12.5}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-4 xl:col-2">
          <StatCard 
            title="Total Credit" 
            value={summary?.totalCredit || summary?.TotalCredit || 0} 
            icon="pi pi-arrow-down" 
            color="#F44336"
            trend={true}
            trendValue={-5.2}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-4 xl:col-2">
          <StatCard 
            title="Balance" 
            value={summary?.balance || summary?.Balance || 0} 
            icon="pi pi-chart-line" 
            color="#4CAF50"
            subtitle={summary?.balance >= 0 ? 'Positive Balance' : 'Negative Balance'}
            trend={true}
            trendValue={8.3}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-4 xl:col-2">
          <StatCard 
            title="Total Transactions" 
            value={summary?.totalTransactions || summary?.TotalTransactions || 0} 
            icon="pi pi-receipt" 
            color="#FF9800"
          />
        </div>
        <div className="col-12 md:col-6 lg:col-4 xl:col-2">
          <StatCard 
            title="Pending" 
            value={summary?.pendingTransactions || summary?.PendingTransactions || 0} 
            icon="pi pi-hourglass" 
            color="#9C27B0"
          />
        </div>
        <div className="col-12 md:col-6 lg:col-4 xl:col-2">
          <StatCard 
            title="Total Accounts" 
            value={summary?.totalAccounts || summary?.TotalAccounts || 0} 
            icon="pi pi-users" 
            color="#607D8B"
          />
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid mt-3">
        <div className="col-12 lg:col-7">
          <Card className="chart-card h-full">
            <div className="flex justify-content-between align-items-center mb-3">
              <div>
                <h3 className="m-0">Transaction Trends</h3>
                <p className="text-500 text-sm m-0 mt-1">Monthly debit vs credit comparison</p>
              </div>
              <Badge value={`Last ${selectedPeriod} months`} severity="info"></Badge>
            </div>
            <div style={{ height: '350px' }}>
              {lineChartData.datasets && (
                <Chart type="line" data={lineChartData} options={chartOptions} />
              )}
            </div>
          </Card>
        </div>

        <div className="col-12 lg:col-5">
          <div className="grid">
            <div className="col-12">
              <Card className="chart-card">
                <h3 className="m-0 mb-3">Top Accounts by Balance</h3>
                <div style={{ height: '250px' }}>
                  {pieChartData.datasets && (
                    <Chart type="pie" data={pieChartData} options={chartOptions} />
                  )}
                </div>
              </Card>
            </div>
            <div className="col-12 mt-3">
              <Card className="chart-card">
                <h3 className="m-0 mb-3">Account Balances</h3>
                <div style={{ height: '200px' }}>
                  {barChartData.datasets && (
                    <Chart type="bar" data={barChartData} options={chartOptions} />
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="grid mt-3">
        <div className="col-12">
          <Card className="transactions-card">
            <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
              <div>
                <h3 className="m-0">Recent Transactions</h3>
                <p className="text-500 text-sm m-0 mt-1">Latest financial activities</p>
              </div>
              <Button 
                label="View All" 
                icon="pi pi-arrow-right" 
                iconPos="right" 
                className="p-button-text"
                onClick={() => window.location.href = '/transactions'}
              />
            </div>
            <DataTable 
              value={recentTransactions} 
              paginator 
              rows={5} 
              responsiveLayout="scroll"
              emptyMessage="No transactions found"
              className="p-datatable-sm"
              showGridlines
              rowHover
            >
              <Column field="transactionId" header="ID" style={{ width: '8%' }} sortable></Column>
              <Column field="transactionDate" header="Date" body={transactionDateTemplate} style={{ width: '15%' }} sortable></Column>
              <Column field="voucherRefNo" header="Voucher No." body={voucherTemplate} style={{ width: '20%' }} sortable></Column>
              <Column field="narration" header="Narration" style={{ width: '35%' }}></Column>
              <Column field="totalAmount" header="Amount" body={transactionAmountTemplate} style={{ width: '12%' }} sortable></Column>
              <Column field="status" header="Status" body={statusTemplate} style={{ width: '10%' }}></Column>
            </DataTable>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;